<template>
  <div
    class="previewStreamViewport d-flex justify-center align-center"
    style="height: 100%">
    <div
      v-if="isConnecting"
      class="d-flex flex-column align-center">
      <v-progress-circular
        indeterminate
        color="primary"></v-progress-circular>
      <span class="mt-2 text-caption">Attempting to Connect...</span>
    </div>
    <!-- Orb MJPEG: JS-parsed stream rendered to <img> via blob URLs -->
    <img
      v-if="directMjpeg"
      ref="directImgRef"
      class="direct-mjpeg"
      v-show="!isConnecting" />
    <canvas
      ref="canvasRef"
      v-show="!directMjpeg && !isConnecting"></canvas>
    <!-- Subtle MJPEG fallback indicator -->
    <span
      v-if="state.streamMode !== 'webrtc' && !isConnecting"
      class="stream-mode-indicator"
      :title="state.streamMode === 'mse' ? 'Using MSE/WebSocket (WebRTC unavailable)' : 'Using MJPEG fallback (WebRTC unavailable)'">
      {{ state.streamMode === 'mse' ? 'MSE' : 'MJPEG' }}
    </span>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { state } from '@/stores/deluge.js';

const props = defineProps({
  port: {
    type: [Number, String],
    default: '',
  },
  output: {
    type: [Number, String],
    default: 0,
  },
  frameNo: {
    type: [Number, String],
    default: 0,
  },
  // Unique key to force full component remount on errors
  instanceKey: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['request-reset']);

const canvasRef = ref(null);
const directImgRef = ref(null);
const isConnecting = ref(true);

// Orb MJPEG: bypass the browser's native MJPEG <img> rendering (which
// Chromium throttles to ~1fps on Linux) by parsing the multipart stream
// in JavaScript and updating an <img> via blob URLs.
const directMjpeg = computed(() => state.is_orb && state.streamMode === 'mjpeg');
let mjpegAbort = null;
let prevBlobUrl = null;

function stopMjpegStream() {
  if (mjpegAbort) {
    mjpegAbort.abort();
    mjpegAbort = null;
  }
  if (prevBlobUrl) {
    URL.revokeObjectURL(prevBlobUrl);
    prevBlobUrl = null;
  }
}

async function startMjpegStream() {
  stopMjpegStream();
  const playerRef = state.playerRef;
  if (!playerRef || playerRef.tagName !== 'IMG' || !playerRef.src) return;
  const url = playerRef.src;
  const img = directImgRef.value;
  if (!img) return;

  const controller = new AbortController();
  mjpegAbort = controller;

  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok || !res.body) return;

    const reader = res.body.getReader();
    let buf = new Uint8Array(0);

    // JPEG markers: FFD8 = start, FFD9 = end
    const SOI = 0xffd8;
    const EOI = 0xffd9;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Append chunk to buffer
      const next = new Uint8Array(buf.length + value.length);
      next.set(buf);
      next.set(value, buf.length);
      buf = next;

      // Extract all complete JPEG frames from buffer
      let searchFrom = 0;
      while (searchFrom < buf.length - 1) {
        // Find JPEG SOI (FF D8)
        let soiIdx = -1;
        for (let i = searchFrom; i < buf.length - 1; i++) {
          if (buf[i] === 0xff && buf[i + 1] === 0xd8) {
            soiIdx = i;
            break;
          }
        }
        if (soiIdx === -1) break;

        // Find JPEG EOI (FF D9) after SOI
        let eoiIdx = -1;
        for (let i = soiIdx + 2; i < buf.length - 1; i++) {
          if (buf[i] === 0xff && buf[i + 1] === 0xd9) {
            eoiIdx = i + 2; // include the EOI marker
            break;
          }
        }
        if (eoiIdx === -1) break;

        // Got a complete JPEG frame
        const jpeg = buf.slice(soiIdx, eoiIdx);
        const blob = new Blob([jpeg], { type: 'image/jpeg' });
        const blobUrl = URL.createObjectURL(blob);
        img.src = blobUrl;
        isConnecting.value = false;

        if (prevBlobUrl) URL.revokeObjectURL(prevBlobUrl);
        prevBlobUrl = blobUrl;

        // Advance past this frame
        searchFrom = eoiIdx;
      }

      // Keep only unprocessed data
      if (searchFrom > 0) {
        buf = buf.slice(searchFrom);
      }
    }
  } catch (e) {
    if (e.name !== 'AbortError') {
      console.error('[PreviewStream] MJPEG stream error:', e);
    }
  }
}

// Rate-limit how often PreviewStream requests a full reset to avoid
// causing parent components to remount repeatedly when the video is
// transiently stalled or when multiple errors fire in quick succession.
const RESET_COOLDOWN = 3000; // ms
let lastResetRequest = 0;
function emitResetDebounced() {
  const now = Date.now();
  if (now - lastResetRequest < RESET_COOLDOWN) {
    console.debug('[PreviewStream] suppressed duplicate reset request');
    return;
  }
  lastResetRequest = now;
  emit('request-reset');
}

const FRAME_WIDTH = 384;
const FRAME_HEIGHT = 216;

// Prefer requestVideoFrameCallback: it fires only when the video produces a new
// frame which reduces wasted draws and latency. Use createImageBitmap when
// available to offload copying to the browser and avoid blocking the main
// thread. Keep a RAF-based fallback when APIs aren't present.

let vfcHandle = null;
let rafId = null;
let fallbackRunning = false;
let fpsInterval = 1000 / 25;
let then = Date.now();
let worker = null;
let offscreenAttached = false;
let videoElement = null;
let isImageSource = false;

function getSourceDimensions(el) {
  if (el.tagName === 'IMG') {
    return { width: el.naturalWidth, height: el.naturalHeight };
  }
  return { width: el.videoWidth, height: el.videoHeight };
}

function drawFrameFromVideo(video, ctx) {
  if (!video) return;

  // For Orb mode, draw the entire video frame at its actual dimensions.
  // The sidecar scales to 384xN preserving the window's aspect ratio — the
  // canvas must match those dimensions, not the hardcoded 16:9 FRAME_HEIGHT.
  // For Ventuz mode, slice the stacked frame based on frameNo.
  const isOrbMode = state.is_orb;
  const dims = getSourceDimensions(video);
  const sourceY = isOrbMode ? 0 : props.frameNo * FRAME_HEIGHT;
  const sourceHeight = isOrbMode ? dims.height : FRAME_HEIGHT;
  const sourceWidth = isOrbMode ? dims.width : FRAME_WIDTH;
  const destW = isOrbMode ? sourceWidth : FRAME_WIDTH;
  const destH = isOrbMode ? sourceHeight : FRAME_HEIGHT;

  // In Orb mode, resize canvas to match source if dimensions changed
  if (isOrbMode && ctx && sourceWidth && sourceHeight) {
    if (ctx.canvas.width !== sourceWidth || ctx.canvas.height !== sourceHeight) {
      ctx.canvas.width = sourceWidth;
      ctx.canvas.height = sourceHeight;
    }
  }

  try {
    if (offscreenAttached && worker && window.createImageBitmap) {
      // createImageBitmap + worker: create bitmap and post to worker (transfer)
      createImageBitmap(video, 0, sourceY, sourceWidth, sourceHeight)
        .then((bitmap) => {
          try {
            // In Orb mode, tell worker to resize if needed
            if (isOrbMode && sourceWidth && sourceHeight) {
              worker.postMessage({ type: 'resize', width: sourceWidth, height: sourceHeight });
            }
            worker.postMessage({ type: 'frame', bitmap }, [bitmap]);
          } catch (e) {
            // If transfer failed, fallback to drawing on main thread
            try {
              ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
              ctx.drawImage(bitmap, 0, 0);
            } finally {
              try {
                bitmap.close();
              } catch (e) {}
            }
          }
        })
        .catch((_) => {
          // fallback to main-thread drawImage
          try {
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
            ctx.drawImage(
              video,
              0,
              sourceY,
              sourceWidth,
              sourceHeight,
              0,
              0,
              destW,
              destH
            );
          } catch (e) {}
        });
    } else if (window.createImageBitmap) {
      // createImageBitmap on the main thread and draw (requires ctx)
      if (!ctx) return;
      createImageBitmap(video, 0, sourceY, sourceWidth, sourceHeight)
        .then((bitmap) => {
          try {
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
            ctx.drawImage(bitmap, 0, 0);
          } finally {
            try {
              bitmap.close();
            } catch (e) {}
          }
        })
        .catch((_) => {
          try {
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
            ctx.drawImage(
              video,
              0,
              sourceY,
              sourceWidth,
              sourceHeight,
              0,
              0,
              destW,
              destH
            );
          } catch (e) {}
        });
    } else {
      if (!ctx) return;
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.drawImage(
        video,
        0,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        destW,
        destH
      );
    }
  } catch (e) {
    // drawImage/createImageBitmap may fail if the video isn't ready; ignore.
  }
}

function frameCallback(now, metadata) {
  if (!videoElement || !canvasRef.value) return;

  if (offscreenAttached) {
    drawFrameFromVideo(videoElement, null);
  } else {
    try {
      const ctx = canvasRef.value.getContext('2d');
      if (ctx) drawFrameFromVideo(videoElement, ctx);
    } catch (e) {
      // Canvas corrupted - request full reset
      console.error('[PreviewStream] Canvas corrupted, requesting reset');
      emitResetDebounced();
      return;
    }
  }

  try {
    vfcHandle = videoElement.requestVideoFrameCallback(frameCallback);
  } catch (e) {
    startRAFLoop();
  }
}

function startRAFLoop() {
  if (fallbackRunning) return;
  fallbackRunning = true;
  then = Date.now();

  function animate() {
    rafId = requestAnimationFrame(animate);
    const now = Date.now();
    const elapsed = now - then;
    if (elapsed > fpsInterval) {
      then = now - (elapsed % fpsInterval);
      if (!videoElement || !canvasRef.value) return;
      if (isImageSource || (!videoElement.paused && !videoElement.ended)) {
        if (offscreenAttached) {
          drawFrameFromVideo(videoElement, null);
        } else {
          try {
            const ctx = canvasRef.value.getContext('2d');
            if (ctx) drawFrameFromVideo(videoElement, ctx);
          } catch (e) {
            console.error(
              '[PreviewStream] Canvas corrupted in RAF, requesting reset'
            );
            emitResetDebounced();
            stopRAFLoop();
            return;
          }
        }
      }
    }
  }

  animate();
}

function stopRAFLoop() {
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  fallbackRunning = false;
}

function stopVFC() {
  if (
    videoElement &&
    vfcHandle != null &&
    videoElement.cancelVideoFrameCallback
  ) {
    try {
      videoElement.cancelVideoFrameCallback(vfcHandle);
    } catch (e) {}
  }
  vfcHandle = null;
}

// Video event handlers
const onPlaying = () => {
  console.debug('[PreviewStream] video playing event');
  isConnecting.value = false;
  if (videoElement && videoElement.requestVideoFrameCallback) {
    stopRAFLoop();
    stopVFC();
    vfcHandle = videoElement.requestVideoFrameCallback(frameCallback);
  } else {
    startRAFLoop();
  }
};

const onPauseOrEnd = () => {
  console.debug('[PreviewStream] video paused or ended');
  isConnecting.value = true;
  stopVFC();
  stopRAFLoop();
};

const onError = () => {
  // Video error - request full reset
  console.debug('[PreviewStream] Video error, requesting reset');
  emitResetDebounced();
};

function attachSource(el) {
  console.debug('[PreviewStream] attachSource called', {
    hasElement: Boolean(el),
    tagName: el?.tagName,
    readyState: el?.readyState,
  });
  if (videoElement) {
    videoElement.removeEventListener('playing', onPlaying);
    videoElement.removeEventListener('pause', onPauseOrEnd);
    videoElement.removeEventListener('ended', onPauseOrEnd);
    videoElement.removeEventListener('error', onError);
    videoElement.removeEventListener('load', onImageLoad);
  }

  videoElement = el;
  isImageSource = el?.tagName === 'IMG';

  if (videoElement) {
    videoElement.addEventListener('error', onError);

    if (isImageSource) {
      // MJPEG <img> - listen for load event and start RAF loop
      videoElement.addEventListener('load', onImageLoad);
      // If image already has content, start immediately
      if (videoElement.naturalWidth > 0) {
        onImageLoad();
      }
    } else {
      // <video> element - use standard video events
      videoElement.addEventListener('playing', onPlaying);
      videoElement.addEventListener('pause', onPauseOrEnd);
      videoElement.addEventListener('ended', onPauseOrEnd);

      // If already playing, start loop
      if (
        !videoElement.paused &&
        !videoElement.ended &&
        videoElement.readyState > 2
      ) {
        onPlaying();
      }
    }
  }
}

const onImageLoad = () => {
  console.debug('[PreviewStream] image load event');
  isConnecting.value = false;
  stopVFC();
  startRAFLoop();
};

function resolveSourceElement(ref) {
  if (!ref) return null;
  if (ref.tagName === 'VIDEO' || ref.tagName === 'IMG') return ref;
  return ref.children?.[0] || null;
}

function init() {
  // Orb MJPEG: parse the multipart stream in JS, render via blob URLs
  if (directMjpeg.value) {
    console.debug('[PreviewStream] init: JS MJPEG parser (Orb)');
    startMjpegStream();
    return;
  }

  const ref = state.playerRef;
  const source = resolveSourceElement(ref);
  const canvas = canvasRef.value;

  if (!source || !canvas) {
    console.debug('[PreviewStream] init: waiting for source or canvas');
    return;
  }

  console.debug('[PreviewStream] init: attaching to', source.tagName);

  // Set canvas dimensions
  try {
    canvas.width = FRAME_WIDTH;
    canvas.height = FRAME_HEIGHT;
  } catch (e) {
    // Canvas already transferred - should never happen on fresh mount
    console.error('[PreviewStream] Canvas corrupted on init, requesting reset');
    emitResetDebounced();
    return;
  }

  // Try OffscreenCanvas + Worker
  if (window.OffscreenCanvas && typeof Worker !== 'undefined') {
    try {
      worker = new Worker(
        new URL('@/workers/previewWorker.js', import.meta.url)
      );
      worker.onmessage = (ev) => {
        if (ev.data?.type === 'stalled') tryReloadVideo();
      };
      const offscreen = canvas.transferControlToOffscreen();
      worker.postMessage(
        {
          type: 'init',
          canvas: offscreen,
          width: FRAME_WIDTH,
          height: FRAME_HEIGHT,
        },
        [offscreen]
      );
      offscreenAttached = true;
      console.debug('[PreviewStream] OffscreenCanvas worker created');
    } catch (e) {
      console.error('[PreviewStream] Worker creation failed:', e);
      offscreenAttached = false;
      if (worker) {
        try {
          worker.terminate();
        } catch (e) {}
        worker = null;
      }
    }
  }

  attachSource(source);
}

onMounted(() => {
  console.debug('[PreviewStream] mounted, instanceKey:', props.instanceKey);
  init();

  // Watch for playerRef or stream mode changes (e.g. WebRTC -> MJPEG fallback)
  watch(
    () => [state.playerRef, state.streamMode],
    () => {
      console.debug('[PreviewStream] playerRef/streamMode changed, re-initializing');
      stopVFC();
      stopRAFLoop();
      stopMjpegStream();
      if (directMjpeg.value) {
        startMjpegStream();
        return;
      }
      attachSource(resolveSourceElement(state.playerRef));
    }
  );
});

onUnmounted(() => {
  console.debug('[PreviewStream] unmounting');
  stopVFC();
  stopRAFLoop();
  stopMjpegStream();
  if (worker) {
    try {
      worker.terminate();
    } catch (e) {}
    worker = null;
  }
  videoElement = null;
  offscreenAttached = false;
});

const host = window.location.hostname;
// Build stream URL: if props.port is provided, use it. Otherwise fall back to current page port.
// Omit the port if it matches the default for the protocol (80 for ws, 443 for wss).
const streamUrl = computed(() => {
  const useProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const portToUse = props.port || window.location.port;
  const defaultPort = useProtocol === 'wss' ? '443' : '80';
  const portSegment =
    portToUse && String(portToUse) !== '' && String(portToUse) !== defaultPort
      ? `:${portToUse}`
      : '';
  return `${useProtocol}://${host}${portSegment}/remoting/2.0/streamoutws?o=${props.output}`;
});

let lastReloadAttempt = 0;
const RELOAD_COOLDOWN = 3000;

function tryReloadVideo() {
  const now = Date.now();
  if (now - lastReloadAttempt < RELOAD_COOLDOWN) return;
  lastReloadAttempt = now;

  // Just request a full reset instead of trying to fix it
  console.debug('[PreviewStream] Stream stalled, requesting reset');
  emitResetDebounced();
}
</script>

<style scoped>
.previewStreamViewport {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 5px;
  background-color: rgba(0, 0, 0, 0.1);
}

.stream-mode-indicator {
  position: absolute;
  bottom: 4px;
  right: 4px;
  font-size: 9px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  background: rgba(0, 0, 0, 0.3);
  padding: 1px 4px;
  border-radius: 2px;
  pointer-events: none;
  user-select: none;
}

canvas,
.direct-mjpeg {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain; /* Never stretch, always fit */
}
</style>
